import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api/contexto'
import { ErrorApi } from '../api/errores'

/** Un extra del pedido acordado (pack/suelto) con su cantidad. */
export interface ExtraAcordado {
  productoId: number | null
  nombre: string
  precio: number | null
  cantidad: number
}

export interface GuardarPedidoAcordado {
  bono?: number | null
  litraje?: string | null
  diaRuta?: string | null
  extras?: ExtraAcordado[]
}

export type SemanaCiclo = 's1' | 's2' | 's3' | 's4'

/** Resultado del envío manual del aviso (botón del panel). */
export interface ResultadoAviso {
  estado: 'enviado' | 'simulado' | 'sin_telefono' | 'filtrado' | 'ya_respondio' | 'fallo'
  mensaje: string
}

/**
 * Guarda el pedido acordado (recurrente) de un cliente. Es lo que manda sobre el
 * último albarán en la variable {{2}} del aviso. NO crea albarán (eso es
 * "Revisar y publicar").
 */
export function useGuardarPedidoAcordado() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation<unknown, ErrorApi, { contactId: string; datos: GuardarPedidoAcordado }>({
    mutationFn: ({ contactId, datos }) =>
      api.pedir(`/api/pedido-acordado/${encodeURIComponent(contactId)}`, { metodo: 'POST', cuerpo: datos }),
    onSuccess: (_d, { contactId }) => {
      qc.invalidateQueries({ queryKey: ['pedido-acordado', contactId] })
    },
  })
}

/** Fija la semana del ciclo del cliente (s1..s4). */
export function useFijarSemana() {
  const api = useApi()
  return useMutation<unknown, ErrorApi, { contactId: string; semana: SemanaCiclo }>({
    mutationFn: ({ contactId, semana }) =>
      api.pedir(`/api/pedido-acordado/${encodeURIComponent(contactId)}/semana`, {
        metodo: 'POST',
        cuerpo: { semana },
      }),
  })
}

/** Envía el aviso a este cliente ahora (botón "Enviar aviso"). */
export function useEnviarAviso() {
  const api = useApi()
  return useMutation<ResultadoAviso, ErrorApi, { contactId: string }>({
    mutationFn: ({ contactId }) =>
      api.pedir<ResultadoAviso>(`/api/ciclo/enviar-aviso/${encodeURIComponent(contactId)}`, {
        metodo: 'POST',
      }),
  })
}
